import React, { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import PageHeader from "../../components/common/PageHeader";
import Philosophy from "./Philosophy";
import { useXhr } from "hooks/useXhr";
import { usePhilosophyListContext } from "hooks/usePhilosophyListContext";
import { usePhilosophyContext } from "hooks/usePhilosophyContext";
import { Button, Spinner } from "react-bootstrap";
import 'react-quill/dist/quill.snow.css';
import {
    DndContext,
    closestCenter,
    PointerSensor,
    useSensor,
    useSensors
} from "@dnd-kit/core";
import {
    arrayMove,
    SortableContext,
    useSortable,
    verticalListSortingStrategy,
    rectSortingStrategy
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

function PhilosophyList() {
    //States
    const [item, setItem] = useState({
        photo: null,
        alt: null,
        title: null,
        titleEng: null,
        titleFra: null,
        titleGer: null,
        titleDut: null,
        text1: null,
        textEng: null,
        textFra: null,
        textGer: null,
        textDut: null,
        position: null,
    });
    const [status, setStatus] = useState(false);
    const [isUpdate, setIsUpdate] = useState(null);
    const [isPhilosophy, setIsPhilosophy] = useState(null);
    const [lang, setLang] = useState(null);
    const [langPhilosophy, setLangPhilosophy] = useState(null);
    const [isDelete, setIsDelete] = useState(null);
    const [isModal, setIsModal] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState({
        field: null,
        note: null
    });
    const [sortableIds, setSortableIds] = useState([]);

    //Hooks
    const { customXhr } = useXhr()
    const { philosophyList, dispatch } = usePhilosophyListContext();
    const { philosophy, dispatch: philosophyDispatch } = usePhilosophyContext();

    const requiredFields = ['alt', 'title', 'text1', 'position'];
    const requiredFieldsForPhilosophy = ['title', 'text1', 'text2'];
    const maxTextLength = 350;

    const handleOnChange = (e) => {
        const { id, value } = e.target;

        setItem(prevState => ({
            ...prevState,
            [id]: value
        }));
    };

    const handleOnUpdateChange = (e) => {
        const { id, value } = e.target;

        setIsUpdate(prevState => ({
            ...prevState,
            [id]: value
        }));
    };

    const handleOnPhilosophyChange = (e) => {
        const { id, value } = e.target;

        setIsPhilosophy(prevState => ({
            ...prevState,
            [id]: value
        }));
    };

    const generateUniqueFileName = (file) => {
        const timestamp = Date.now();
        const randomNumber = Math.floor(Math.random() * 10000);
        const extension = file.name.split('.').pop();

        return `${timestamp}_${randomNumber}.${extension}`;
    };

    const handlePhotoInput = (e) => {
        const file = e.target.files[0];
        const id = e.target.id;

        const newFileName = generateUniqueFileName(file);
        const newFile = new File([file], newFileName, { type: file.type });

        if (isUpdate) {
            setIsUpdate(prevState => ({
                ...prevState,
                [id]: newFile
            }));
        } else {
            setItem(prevState => ({
                ...prevState,
                [id]: newFile
            }));
        }
    };

    const handleDelete = async (id) => {
        setIsLoading(true);
        const formData = new FormData();
        formData.append('module', 'philosophy_list');
        formData.append('action', 'delete');
        formData.append('id', id);

        const response = await customXhr(formData);

        if (response.status === "success") {
            setStatus(!status);
            setIsDelete(null);
            setIsLoading(false);
        };
    };

    const handleUpdate = async () => {
        for (const field of requiredFields) {
            if (!isUpdate[field] || isUpdate[field] === "") {
                return (
                    setError({
                        field: field,
                        note: `Zorunlu alan`
                    }),
                    setIsLoading(false)
                )
            }
        }
        setIsLoading(true);
        const formData = new FormData();
        formData.append('module', 'philosophy_list');
        formData.append('action', 'update');
        formData.append('id', isUpdate.id);
        formData.append('photo', isUpdate.photo);
        formData.append('alt', isUpdate.alt);
        formData.append('title', isUpdate.title);
        formData.append('titleEng', isUpdate.titleEng);
        formData.append('titleFra', isUpdate.titleFra);
        formData.append('titleGer', isUpdate.titleGer);
        formData.append('titleDut', isUpdate.titleDut);
        formData.append('text1', isUpdate.text1);
        formData.append('textEng', isUpdate.textEng);
        formData.append('textFra', isUpdate.textFra);
        formData.append('textGer', isUpdate.textGer);
        formData.append('textDut', isUpdate.textDut);
        formData.append('position', isUpdate.position);

        const response = await customXhr(formData);

        if (response.status === "success") {
            setIsUpdate(null);
            setLang(null);
            setStatus(!status);
            setError({
                field: null,
                note: null
            });
            setIsLoading(false);
        };
    };

    const handlePhilosophy = async () => {
        for (const field of requiredFieldsForPhilosophy) {
            if (!isPhilosophy[field] || isPhilosophy[field] === "") {
                return (
                    setError({
                        field: field,
                        note: `Zorunlu alan`
                    }),
                    setIsLoading(false)
                )
            }
        }
        setIsLoading(true);
        const formData = new FormData();
        formData.append('module', 'philosophy');
        formData.append('action', 'update');
        formData.append('id', isPhilosophy.id);
        formData.append('title', isPhilosophy.title);
        formData.append('titleEng', isPhilosophy.titleEng);
        formData.append('titleFra', isPhilosophy.titleFra);
        formData.append('titleGer', isPhilosophy.titleGer);
        formData.append('titleDut', isPhilosophy.titleDut);
        formData.append('text1', isPhilosophy.text1);
        formData.append('textEng1', isPhilosophy.textEng1);
        formData.append('textFra1', isPhilosophy.textFra1);
        formData.append('textGer1', isPhilosophy.textGer1);
        formData.append('textDut1', isPhilosophy.textDut1);
        formData.append('text2', isPhilosophy.text2);
        formData.append('textEng2', isPhilosophy.textEng2);
        formData.append('textFra2', isPhilosophy.textFra2);
        formData.append('textGer2', isPhilosophy.textGer2);
        formData.append('textDut2', isPhilosophy.textDut2);

        const response = await customXhr(formData);

        if (response.status === "success") {
            setIsPhilosophy(null);
            setLangPhilosophy(null);
            setStatus(!status);
            setError({
                field: null,
                note: null
            });
            setIsLoading(false);
        };
    };

    const handleSubmit = async () => {
        for (const field of requiredFields) {
            if (!item[field] || item[field] === "") {
                return (
                    setError({
                        field: field,
                        note: `Zorunlu alan`
                    }),
                    setIsLoading(false)
                )
            }
        }
        setIsLoading(true);
        const formData = new FormData();
        formData.append('module', 'philosophy_list');
        formData.append('action', 'insert');
        formData.append('photo', item.photo);
        formData.append('alt', item.alt);
        formData.append('title', item.title);
        formData.append('titleEng', item.titleEng);
        formData.append('titleFra', item.titleFra);
        formData.append('titleGer', item.titleGer);
        formData.append('titleDut', item.titleDut);
        formData.append('text1', item.text1);
        formData.append('textEng', item.textEng);
        formData.append('textFra', item.textFra);
        formData.append('textGer', item.textGer);
        formData.append('textDut', item.textDut);
        formData.append('position', item.position);

        const response = await customXhr(formData);

        if (response.status === "success") {
            setItem({
                photo: null,
                alt: null,
                title: null,
                titleEng: null,
                titleFra: null,
                titleGer: null,
                titleDut: null,
                text1: null,
                textEng: null,
                textFra: null,
                textGer: null,
                textDut: null,
                position: null,
            });
            setIsModal(null);
            setStatus(!status);
            setError({
                field: null,
                note: null
            });
            setIsLoading(false);
        };
    };

    const getPhilosophyList = async () => {
        const formData = new FormData();
        formData.append('module', 'philosophy_list');
        formData.append('action', 'list');

        const response = await customXhr(formData);

        if (response) {
            dispatch({ type: 'SET_PHILOSOPHY_LIST', payload: response });
            setItem(prevState => ({
                ...prevState,
                position: response.length > 0 ? response.length + 1 : 1
            }));
            getPhilosophy();
        };
    };

    const getPhilosophy = async () => {
        const formData = new FormData();
        formData.append('module', 'philosophy');
        formData.append('action', 'list');

        const response = await customXhr(formData);

        if (response) {
            philosophyDispatch({ type: 'SET_PHILOSOPHY', payload: response[0] });
        };
    };

    useEffect(() => {
        getPhilosophyList();
    }, [status]);

    useEffect(() => {
        if (philosophyList && philosophyList.length > 0) {
            setSortableIds(philosophyList.map(item => item.id.toString()));
        }
    }, [philosophyList]);

    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
    );

    const handleDragEnd = (event) => {
        const { active, over } = event;
        if (active.id !== over?.id) {
            setSortableIds((items) => {
                const oldIndex = items.indexOf(active.id);
                const newIndex = items.indexOf(over.id);
                const newOrder = arrayMove(items, oldIndex, newIndex);
                updateOrderOnBackend(newOrder);
                return newOrder;
            });
        }
    };

    const updateOrderOnBackend = async (newOrder) => {
        const formData = new FormData();
        formData.append('module', 'philosophy_list');
        formData.append('action', 'sort');
        // Sıralamayı id ve yeni position ile gönderiyoruz
        // newOrder: ["3", "1", "2", ...]
        newOrder.forEach((id, index) => {
            formData.append(`order[${index}][id]`, id);
            formData.append(`order[${index}][position]`, index + 1);
        });

        await customXhr(formData);
    };

    const lengthError = (field) => {
        if (isUpdate && isUpdate[field] && maxTextLength - isUpdate[field].length < 20) {
            return <span className="text-danger">
                {`${maxTextLength - (isUpdate && isUpdate[field] ? isUpdate[field].length : 0)} harf hakkınız kaldı!`}
            </span>;
        } else {
            return "";
        }
    };

    return (
        <div className="container-xxl">
            <PageHeader headerTitle="Kalite ve Gıda Güvenliği" renderRight={() => {
                return <div className="col-auto d-flex w-sm-100">
                    <button className="btn btn-dark btn-set-task w-sm-100 me-3" onClick={() => { setIsPhilosophy(philosophy); setLangPhilosophy("tur"); }}>
                        <img
                            src={process.env.PUBLIC_URL + `/flags/turkey.png`}
                            style={{ width: "20px", marginRight: "5px", border: "2px solid white", borderRadius: "50%" }}
                        />
                        Türkçe Başlık ve Yazı
                    </button>
                    <button className="btn btn-dark btn-set-task w-sm-100 me-3" onClick={() => { setIsPhilosophy(philosophy); setLangPhilosophy("eng"); }}>
                        <img
                            src={process.env.PUBLIC_URL + `/flags/united-kingdom.png`}
                            style={{ width: "20px", marginRight: "5px", border: "2px solid white", borderRadius: "50%" }}
                        />
                        İngilizce Başlık ve Yazı
                    </button>
                    <button className="btn btn-dark btn-set-task w-sm-100 me-3" onClick={() => { setIsPhilosophy(philosophy); setLangPhilosophy("fra"); }}>
                        <img
                            src={process.env.PUBLIC_URL + `/flags/france.png`}
                            style={{ width: "20px", marginRight: "5px", border: "2px solid white", borderRadius: "50%" }}
                        />
                        Fransızca Başlık ve Yazı
                    </button>
                    <button className="btn btn-dark btn-set-task w-sm-100 me-3" onClick={() => { setIsPhilosophy(philosophy); setLangPhilosophy("ger"); }}>
                        <img
                            src={process.env.PUBLIC_URL + `/flags/germany.png`}
                            style={{ width: "20px", marginRight: "5px", border: "2px solid white", borderRadius: "50%" }}
                        />
                        Almanca Başlık ve Yazı
                    </button>
                    <button className="btn btn-dark btn-set-task w-sm-100 me-3" onClick={() => { setIsPhilosophy(philosophy); setLangPhilosophy("dut"); }}>
                        <img
                            src={process.env.PUBLIC_URL + `/flags/netherlands.png`}
                            style={{ width: "20px", marginRight: "5px", border: "2px solid white", borderRadius: "50%" }}
                        />
                        Felemenkçe Başlık ve Yazı
                    </button>
                    <button className="btn btn-dark btn-set-task w-sm-100" onClick={() => { setIsModal(true) }}>
                        <i className="icofont-plus-circle me-2 fs-6"></i>
                        Yeni Öğe
                    </button>
                </div>
            }} />
            <div className="row align-items-center">
                <div className="col-lg-12 col-md-12 flex-column">
                    <DndContext
                        sensors={sensors}
                        collisionDetection={closestCenter}
                        onDragEnd={handleDragEnd}
                    >
                        <SortableContext
                            items={sortableIds}
                            strategy={rectSortingStrategy}
                        >
                            <div className="row g-3 gy-5 py-3 row-deck">
                                {sortableIds.map((id) => {
                                    const d = philosophyList.find(item => item.id.toString() === id);
                                    if (!d) return null;
                                    return (
                                        <SortableItem key={d.id} id={d.id.toString()}>
                                            <Philosophy
                                                id={d.id}
                                                photoName={d.photoName}
                                                alt={d.alt}
                                                title={d.title}
                                                text1={d.text1}
                                                onClickEdit={() => { setIsUpdate(d); setLang("tur") }}
                                                onClickEditEng={() => { setIsUpdate(d); setLang("eng") }}
                                                onClickEditFra={() => { setIsUpdate(d); setLang("fra") }}
                                                onClickEditGer={() => { setIsUpdate(d); setLang("ger") }}
                                                onClickEditDut={() => { setIsUpdate(d); setLang("dut") }}
                                                onClickDelete={() => { setIsDelete(d); }}
                                            />
                                        </SortableItem>
                                    );
                                })}
                            </div>
                        </SortableContext>
                    </DndContext>
                </div>
            </div>
            <Modal
                show={isModal}
                onHide={() => {
                    setIsModal(null);
                    setItem(null);
                    setError({
                        field: null,
                        note: null
                    });
                    setIsLoading(false);
                }}
            >
                <Modal.Header closeButton>
                    <Modal.Title className="fw-bold">Yeni Öğe</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="mb-3">
                        <label htmlFor="title" className="form-label">Başlık</label>
                        <input
                            type="text"
                            className="form-control"
                            id="title"
                            value={item && item.title}
                            onChange={handleOnChange}
                            style={{ borderColor: `${error.field === "title" ? "red" : "var(--border-color)"}` }}
                        />
                    </div>
                    {error.field === "title" &&
                        <label className="form-label" style={{ color: "red" }}>{error.note}</label>
                    }
                    <div className="mb-3">
                        <label htmlFor="text1" className="form-label">
                            {"Yazı "}
                            {lengthError("text1")}
                        </label>
                        <textarea
                            className="form-control"
                            id="text1"
                            rows="5"
                            value={item && item.text1}
                            onChange={handleOnChange}
                            maxLength={maxTextLength}
                            style={{ borderColor: `${error.field === "text1" ? "red" : "var(--border-color)"}` }}
                        ></textarea>
                    </div>
                    {error.field === "text1" &&
                        <label className="form-label" style={{ color: "red" }}>{error.note}</label>
                    }
                    <div className="mb-3">
                        <label htmlFor="alt" className="form-label">
                            {"Seo Kelimeler "}
                            <span className="text-danger">
                                (Lütfen 5 Kelimeden Fazla Yazmayınız!)
                            </span>
                        </label>
                        <input
                            type="text"
                            className="form-control"
                            id="alt"
                            value={item && item.alt}
                            onChange={handleOnChange}
                            style={{ borderColor: `${error.field === "alt" ? "red" : "var(--border-color)"}` }}
                        />
                    </div>
                    {error.field === "alt" &&
                        <label className="form-label" style={{ color: "red" }}>{error.note}</label>
                    }
                    <div className="mb-3">
                        <label htmlFor="photo" className="form-label">Fotoğraf Yükle</label>
                        <input
                            className="form-control"
                            type="file"
                            accept="image/svg+xml"
                            id="photo"
                            onChange={handlePhotoInput}
                        />
                        <div className="form-label mt-1" style={{ color: "red" }}>
                            {"Bu alana sadece svg uzantılı dosya yükleyebilirsiniz."}
                        </div>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="primary"
                        className="me-1"
                        onClick={() => handleSubmit()}
                        disabled={isLoading}
                    >
                        {
                            isLoading ?
                                <>
                                    <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-1" />
                                    Yükleniyor...
                                </> :
                                <>
                                    Kaydet
                                </>
                        }
                    </Button>
                </Modal.Footer>
            </Modal>
            <Modal
                show={lang === "tur"}
                onHide={() => {
                    setIsUpdate(null);
                    setLang(null);
                    setError({
                        field: null,
                        note: null
                    });
                    setIsLoading(false);
                }}
            >
                <Modal.Header closeButton>
                    <Modal.Title className="fw-bold">Düzenle</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="mb-3">
                        <label htmlFor="title" className="form-label">Başlık</label>
                        <input
                            type="text"
                            className="form-control"
                            id="title"
                            value={isUpdate && isUpdate.title}
                            onChange={handleOnUpdateChange}
                            style={{ borderColor: `${error.field === "title" ? "red" : "var(--border-color)"}` }}
                        />
                    </div>
                    {error.field === "title" &&
                        <label className="form-label" style={{ color: "red" }}>{error.note}</label>
                    }
                    <div className="mb-3">
                        <label htmlFor="text1" className="form-label">
                            {"Yazı "}
                            {lengthError("text1")}
                        </label>
                        <textarea
                            className="form-control"
                            id="text1"
                            rows="5"
                            value={isUpdate && isUpdate.text1}
                            onChange={handleOnUpdateChange}
                            maxLength={maxTextLength}
                            style={{ borderColor: `${error.field === "text1" ? "red" : "var(--border-color)"}` }}
                        ></textarea>
                    </div>
                    {error.field === "text1" &&
                        <label className="form-label" style={{ color: "red" }}>{error.note}</label>
                    }
                    <div className="mb-3">
                        <label htmlFor="alt" className="form-label">
                            {"Seo Kelimeler "}
                            <span className="text-danger">
                                (Lütfen 5 Kelimeden Fazla Yazmayınız!)
                            </span>
                        </label>
                        <input
                            type="text"
                            className="form-control"
                            id="alt"
                            value={isUpdate && isUpdate.alt}
                            onChange={handleOnUpdateChange}
                            style={{ borderColor: `${error.field === "alt" ? "red" : "var(--border-color)"}` }}
                        />
                    </div>
                    {error.field === "alt" &&
                        <label className="form-label" style={{ color: "red" }}>{error.note}</label>
                    }
                    <div className="mb-3">
                        <label htmlFor="photo" className="form-label">Fotoğraf Yükle</label>
                        <input
                            className="form-control"
                            type="file"
                            accept="image/svg+xml"
                            id="photo"
                            onChange={handlePhotoInput}
                        />
                        <div className="form-label" style={{ color: "red" }}>
                            {"Bu alana sadece svg uzantılı dosya yükleyebilirsiniz."}
                        </div>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="primary"
                        className="me-1"
                        onClick={() => handleUpdate()}
                        disabled={isLoading}
                    >
                        {
                            isLoading ?
                                <>
                                    <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-1" />
                                    Yükleniyor...
                                </> :
                                <>
                                    Kaydet
                                </>
                        }
                    </Button>
                </Modal.Footer>
            </Modal>
            <Modal
                show={lang === "eng"}
                onHide={() => {
                    setIsUpdate(null);
                    setLang(null);
                    setError({
                        field: null,
                        note: null
                    });
                    setIsLoading(false);
                }}
            >
                <Modal.Header closeButton>
                    <Modal.Title className="fw-bold">İngilizce Düzenle</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="mb-3">
                        <label htmlFor="titleEng" className="form-label">Başlık</label>
                        <input
                            type="text"
                            className="form-control"
                            id="titleEng"
                            value={isUpdate && isUpdate.titleEng}
                            onChange={handleOnUpdateChange}
                            style={{ borderColor: `${error.field === "titleEng" ? "red" : "var(--border-color)"}` }}
                        />
                    </div>
                    {error.field === "titleEng" &&
                        <label className="form-label" style={{ color: "red" }}>{error.note}</label>
                    }
                    <div className="mb-3">
                        <label htmlFor="textEng" className="form-label">
                            {"Yazı "}
                            {lengthError("textEng")}
                        </label>
                        <textarea
                            className="form-control"
                            id="textEng"
                            rows="5"
                            value={isUpdate && isUpdate.textEng}
                            onChange={handleOnUpdateChange}
                            maxLength={maxTextLength}
                            style={{ borderColor: `${error.field === "textEng" ? "red" : "var(--border-color)"}` }}
                        ></textarea>
                    </div>
                    {error.field === "textEng" &&
                        <label className="form-label" style={{ color: "red" }}>{error.note}</label>
                    }
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="primary"
                        className="me-1"
                        onClick={() => handleUpdate()}
                        disabled={isLoading}
                    >
                        {
                            isLoading ?
                                <>
                                    <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-1" />
                                    Yükleniyor...
                                </> :
                                <>
                                    Kaydet
                                </>
                        }
                    </Button>
                </Modal.Footer>
            </Modal>
            <Modal
                show={lang === "fra"}
                onHide={() => {
                    setIsUpdate(null);
                    setLang(null);
                    setError({
                        field: null,
                        note: null
                    });
                    setIsLoading(false);
                }}
            >
                <Modal.Header closeButton>
                    <Modal.Title className="fw-bold">Fransızca Düzenle</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="mb-3">
                        <label htmlFor="titleFra" className="form-label">Başlık</label>
                        <input
                            type="text"
                            className="form-control"
                            id="titleFra"
                            value={isUpdate && isUpdate.titleFra}
                            onChange={handleOnUpdateChange}
                            style={{ borderColor: `${error.field === "titleFra" ? "red" : "var(--border-color)"}` }}
                        />
                    </div>
                    {error.field === "titleFra" &&
                        <label className="form-label" style={{ color: "red" }}>{error.note}</label>
                    }
                    <div className="mb-3">
                        <label htmlFor="textFra" className="form-label">
                            {"Yazı "}
                            {lengthError("textFra")}
                        </label>
                        <textarea
                            className="form-control"
                            id="textFra"
                            rows="5"
                            value={isUpdate && isUpdate.textFra}
                            onChange={handleOnUpdateChange}
                            maxLength={maxTextLength}
                            style={{ borderColor: `${error.field === "textFra" ? "red" : "var(--border-color)"}` }}
                        ></textarea>
                    </div>
                    {error.field === "textFra" &&
                        <label className="form-label" style={{ color: "red" }}>{error.note}</label>
                    }
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="primary"
                        className="me-1"
                        onClick={() => handleUpdate()}
                        disabled={isLoading}
                    >
                        {
                            isLoading ?
                                <>
                                    <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-1" />
                                    Yükleniyor...
                                </> :
                                <>
                                    Kaydet
                                </>
                        }
                    </Button>
                </Modal.Footer>
            </Modal>
            <Modal
                show={lang === "ger"}
                onHide={() => {
                    setIsUpdate(null);
                    setLang(null);
                    setError({
                        field: null,
                        note: null
                    });
                    setIsLoading(false);
                }}
            >
                <Modal.Header closeButton>
                    <Modal.Title className="fw-bold">Almanca Düzenle</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="mb-3">
                        <label htmlFor="titleGer" className="form-label">Başlık</label>
                        <input
                            type="text"
                            className="form-control"
                            id="titleGer"
                            value={isUpdate && isUpdate.titleGer}
                            onChange={handleOnUpdateChange}
                            style={{ borderColor: `${error.field === "titleGer" ? "red" : "var(--border-color)"}` }}
                        />
                    </div>
                    {error.field === "titleGer" &&
                        <label className="form-label" style={{ color: "red" }}>{error.note}</label>
                    }
                    <div className="mb-3">
                        <label htmlFor="textGer" className="form-label">
                            {"Yazı "}
                            {lengthError("textGer")}
                        </label>
                        <textarea
                            className="form-control"
                            id="textGer"
                            rows="5"
                            value={isUpdate && isUpdate.textGer}
                            onChange={handleOnUpdateChange}
                            maxLength={maxTextLength}
                            style={{ borderColor: `${error.field === "textGer" ? "red" : "var(--border-color)"}` }}
                        ></textarea>
                    </div>
                    {error.field === "textGer" &&
                        <label className="form-label" style={{ color: "red" }}>{error.note}</label>
                    }
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="primary"
                        className="me-1"
                        onClick={() => handleUpdate()}
                        disabled={isLoading}
                    >
                        {
                            isLoading ?
                                <>
                                    <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-1" />
                                    Yükleniyor...
                                </> :
                                <>
                                    Kaydet
                                </>
                        }
                    </Button>
                </Modal.Footer>
            </Modal>
            <Modal
                show={lang === "dut"}
                onHide={() => {
                    setIsUpdate(null);
                    setLang(null);
                    setError({
                        field: null,
                        note: null
                    });
                    setIsLoading(false);
                }}
            >
                <Modal.Header closeButton>
                    <Modal.Title className="fw-bold">Felemenkçe Düzenle</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="mb-3">
                        <label htmlFor="titleDut" className="form-label">Başlık</label>
                        <input
                            type="text"
                            className="form-control"
                            id="titleDut"
                            value={isUpdate && isUpdate.titleDut}
                            onChange={handleOnUpdateChange}
                            style={{ borderColor: `${error.field === "titleDut" ? "red" : "var(--border-color)"}` }}
                        />
                    </div>
                    {error.field === "titleDut" &&
                        <label className="form-label" style={{ color: "red" }}>{error.note}</label>
                    }
                    <div className="mb-3">
                        <label htmlFor="textDut" className="form-label">
                            {"Yazı "}
                            {lengthError("textDut")}
                        </label>
                        <textarea
                            className="form-control"
                            id="textDut"
                            rows="5"
                            value={isUpdate && isUpdate.textDut}
                            onChange={handleOnUpdateChange}
                            maxLength={maxTextLength}
                            style={{ borderColor: `${error.field === "textDut" ? "red" : "var(--border-color)"}` }}
                        ></textarea>
                    </div>
                    {error.field === "textDut" &&
                        <label className="form-label" style={{ color: "red" }}>{error.note}</label>
                    }
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="primary"
                        className="me-1"
                        onClick={() => handleUpdate()}
                        disabled={isLoading}
                    >
                        {
                            isLoading ?
                                <>
                                    <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-1" />
                                    Yükleniyor...
                                </> :
                                <>
                                    Kaydet
                                </>
                        }
                    </Button>
                </Modal.Footer>
            </Modal>
            <Modal
                show={langPhilosophy === "tur"}
                onHide={() => {
                    setIsPhilosophy(null);
                    setLangPhilosophy(null);
                    setError({
                        field: null,
                        note: null
                    });
                    setIsLoading(false);
                }}
            >
                <Modal.Header closeButton>
                    <Modal.Title className="fw-bold">Başlık ve Yazı Düzenle</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="mb-3">
                        <div className="mb-3">
                            <label htmlFor="title" className="form-label">Başlık</label>
                            <input
                                type="text"
                                className="form-control"
                                id="title"
                                value={isPhilosophy && isPhilosophy.title}
                                onChange={handleOnPhilosophyChange}
                                style={{ borderColor: `${error.field === "title" ? "red" : "var(--border-color)"}` }}
                            />
                        </div>
                        {error.field === "title" &&
                            <label className="form-label" style={{ color: "red" }}>{error.note}</label>
                        }
                        <div className="mb-3">
                            <label htmlFor="text1" className="form-label">Yazı 1</label>
                            <textarea
                                className="form-control"
                                id="text1"
                                rows="5"
                                value={isPhilosophy && isPhilosophy.text1}
                                onChange={handleOnPhilosophyChange}
                                style={{ borderColor: `${error.field === "text1" ? "red" : "var(--border-color)"}` }}
                            ></textarea>
                        </div>
                        {error.field === "text1" &&
                            <label className="form-label" style={{ color: "red" }}>{error.note}</label>
                        }
                        <div className="mb-3">
                            <label htmlFor="text2" className="form-label">Yazı 2</label>
                            <textarea
                                className="form-control"
                                id="text2"
                                rows="5"
                                value={isPhilosophy && isPhilosophy.text2}
                                onChange={handleOnPhilosophyChange}
                                style={{ borderColor: `${error.field === "text2" ? "red" : "var(--border-color)"}` }}
                            ></textarea>
                        </div>
                        {error.field === "text2" &&
                            <label className="form-label" style={{ color: "red" }}>{error.note}</label>
                        }
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="primary"
                        className="me-1"
                        onClick={() => handlePhilosophy()}
                        disabled={isLoading}
                    >
                        {
                            isLoading ?
                                <>
                                    <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-1" />
                                    Yükleniyor...
                                </> :
                                <>
                                    Kaydet
                                </>
                        }
                    </Button>
                </Modal.Footer>
            </Modal>
            <Modal
                show={langPhilosophy === "eng"}
                onHide={() => {
                    setIsPhilosophy(null);
                    setLangPhilosophy(null);
                    setError({
                        field: null,
                        note: null
                    });
                    setIsLoading(false);
                }}
            >
                <Modal.Header closeButton>
                    <Modal.Title className="fw-bold">İngilizce Başlık ve Yazı Düzenle</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="mb-3">
                        <div className="mb-3">
                            <label htmlFor="titleEng" className="form-label">Başlık</label>
                            <input
                                type="text"
                                className="form-control"
                                id="titleEng"
                                value={isPhilosophy && isPhilosophy.titleEng}
                                onChange={handleOnPhilosophyChange}
                                style={{ borderColor: `${error.field === "titleEng" ? "red" : "var(--border-color)"}` }}
                            />
                        </div>
                        {error.field === "titleEng" &&
                            <label className="form-label" style={{ color: "red" }}>{error.note}</label>
                        }
                        <div className="mb-3">
                            <label htmlFor="textEng1" className="form-label">Yazı 1</label>
                            <textarea
                                className="form-control"
                                id="textEng1"
                                rows="5"
                                value={isPhilosophy && isPhilosophy.textEng1}
                                onChange={handleOnPhilosophyChange}
                                style={{ borderColor: `${error.field === "textEng1" ? "red" : "var(--border-color)"}` }}
                            ></textarea>
                        </div>
                        {error.field === "textEng1" &&
                            <label className="form-label" style={{ color: "red" }}>{error.note}</label>
                        }
                        <div className="mb-3">
                            <label htmlFor="textEng2" className="form-label">Yazı 2</label>
                            <textarea
                                className="form-control"
                                id="textEng2"
                                rows="5"
                                value={isPhilosophy && isPhilosophy.textEng2}
                                onChange={handleOnPhilosophyChange}
                                style={{ borderColor: `${error.field === "textEng2" ? "red" : "var(--border-color)"}` }}
                            ></textarea>
                        </div>
                        {error.field === "textEng2" &&
                            <label className="form-label" style={{ color: "red" }}>{error.note}</label>
                        }
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="primary"
                        className="me-1"
                        onClick={() => handlePhilosophy()}
                        disabled={isLoading}
                    >
                        {
                            isLoading ?
                                <>
                                    <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-1" />
                                    Yükleniyor...
                                </> :
                                <>
                                    Kaydet
                                </>
                        }
                    </Button>
                </Modal.Footer>
            </Modal>
            <Modal
                show={langPhilosophy === "fra"}
                onHide={() => {
                    setIsPhilosophy(null);
                    setLangPhilosophy(null);
                    setError({
                        field: null,
                        note: null
                    });
                    setIsLoading(false);
                }}
            >
                <Modal.Header closeButton>
                    <Modal.Title className="fw-bold">Fransızca Başlık ve Yazı Düzenle</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="mb-3">
                        <div className="mb-3">
                            <label htmlFor="titleFra" className="form-label">Başlık</label>
                            <input
                                type="text"
                                className="form-control"
                                id="titleFra"
                                value={isPhilosophy && isPhilosophy.titleFra}
                                onChange={handleOnPhilosophyChange}
                                style={{ borderColor: `${error.field === "titleFra" ? "red" : "var(--border-color)"}` }}
                            />
                        </div>
                        {error.field === "titleFra" &&
                            <label className="form-label" style={{ color: "red" }}>{error.note}</label>
                        }
                        <div className="mb-3">
                            <label htmlFor="textFra1" className="form-label">Yazı 1</label>
                            <textarea
                                className="form-control"
                                id="textFra1"
                                rows="5"
                                value={isPhilosophy && isPhilosophy.textFra1}
                                onChange={handleOnPhilosophyChange}
                                style={{ borderColor: `${error.field === "textFra1" ? "red" : "var(--border-color)"}` }}
                            ></textarea>
                        </div>
                        {error.field === "textFra1" &&
                            <label className="form-label" style={{ color: "red" }}>{error.note}</label>
                        }
                        <div className="mb-3">
                            <label htmlFor="textFra2" className="form-label">Yazı 2</label>
                            <textarea
                                className="form-control"
                                id="textFra2"
                                rows="5"
                                value={isPhilosophy && isPhilosophy.textFra2}
                                onChange={handleOnPhilosophyChange}
                                style={{ borderColor: `${error.field === "textFra2" ? "red" : "var(--border-color)"}` }}
                            ></textarea>
                        </div>
                        {error.field === "textFra2" &&
                            <label className="form-label" style={{ color: "red" }}>{error.note}</label>
                        }
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="primary"
                        className="me-1"
                        onClick={() => handlePhilosophy()}
                        disabled={isLoading}
                    >
                        {
                            isLoading ?
                                <>
                                    <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-1" />
                                    Yükleniyor...
                                </> :
                                <>
                                    Kaydet
                                </>
                        }
                    </Button>
                </Modal.Footer>
            </Modal>
            <Modal
                show={langPhilosophy === "ger"}
                onHide={() => {
                    setIsPhilosophy(null);
                    setLangPhilosophy(null);
                    setError({
                        field: null,
                        note: null
                    });
                    setIsLoading(false);
                }}
            >
                <Modal.Header closeButton>
                    <Modal.Title className="fw-bold">Almanca Başlık ve Yazı Düzenle</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="mb-3">
                        <div className="mb-3">
                            <label htmlFor="titleGer" className="form-label">Başlık</label>
                            <input
                                type="text"
                                className="form-control"
                                id="titleGer"
                                value={isPhilosophy && isPhilosophy.titleGer}
                                onChange={handleOnPhilosophyChange}
                                style={{ borderColor: `${error.field === "titleGer" ? "red" : "var(--border-color)"}` }}
                            />
                        </div>
                        {error.field === "titleGer" &&
                            <label className="form-label" style={{ color: "red" }}>{error.note}</label>
                        }
                        <div className="mb-3">
                            <label htmlFor="textGer1" className="form-label">Yazı 1</label>
                            <textarea
                                className="form-control"
                                id="textGer1"
                                rows="5"
                                value={isPhilosophy && isPhilosophy.textGer1}
                                onChange={handleOnPhilosophyChange}
                                style={{ borderColor: `${error.field === "textGer1" ? "red" : "var(--border-color)"}` }}
                            ></textarea>
                        </div>
                        {error.field === "textGer1" &&
                            <label className="form-label" style={{ color: "red" }}>{error.note}</label>
                        }
                        <div className="mb-3">
                            <label htmlFor="textGer2" className="form-label">Yazı 2</label>
                            <textarea
                                className="form-control"
                                id="textGer2"
                                rows="5"
                                value={isPhilosophy && isPhilosophy.textGer2}
                                onChange={handleOnPhilosophyChange}
                                style={{ borderColor: `${error.field === "textGer2" ? "red" : "var(--border-color)"}` }}
                            ></textarea>
                        </div>
                        {error.field === "textGer2" &&
                            <label className="form-label" style={{ color: "red" }}>{error.note}</label>
                        }
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="primary"
                        className="me-1"
                        onClick={() => handlePhilosophy()}
                        disabled={isLoading}
                    >
                        {
                            isLoading ?
                                <>
                                    <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-1" />
                                    Yükleniyor...
                                </> :
                                <>
                                    Kaydet
                                </>
                        }
                    </Button>
                </Modal.Footer>
            </Modal>
            <Modal
                show={langPhilosophy === "dut"}
                onHide={() => {
                    setIsPhilosophy(null);
                    setLangPhilosophy(null);
                    setError({
                        field: null,
                        note: null
                    });
                    setIsLoading(false);
                }}
            >
                <Modal.Header closeButton>
                    <Modal.Title className="fw-bold">Felemenkçe Başlık ve Yazı Düzenle</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="mb-3">
                        <div className="mb-3">
                            <label htmlFor="titleDut" className="form-label">Başlık</label>
                            <input
                                type="text"
                                className="form-control"
                                id="titleDut"
                                value={isPhilosophy && isPhilosophy.titleDut}
                                onChange={handleOnPhilosophyChange}
                                style={{ borderColor: `${error.field === "titleDut" ? "red" : "var(--border-color)"}` }}
                            />
                        </div>
                        {error.field === "titleDut" &&
                            <label className="form-label" style={{ color: "red" }}>{error.note}</label>
                        }
                        <div className="mb-3">
                            <label htmlFor="textDut1" className="form-label">Yazı 1</label>
                            <textarea
                                className="form-control"
                                id="textDut1"
                                rows="5"
                                value={isPhilosophy && isPhilosophy.textDut1}
                                onChange={handleOnPhilosophyChange}
                                style={{ borderColor: `${error.field === "textDut1" ? "red" : "var(--border-color)"}` }}
                            ></textarea>
                        </div>
                        {error.field === "textDut1" &&
                            <label className="form-label" style={{ color: "red" }}>{error.note}</label>
                        }
                        <div className="mb-3">
                            <label htmlFor="textDut2" className="form-label">Yazı 2</label>
                            <textarea
                                className="form-control"
                                id="textDut2"
                                rows="5"
                                value={isPhilosophy && isPhilosophy.textDut2}
                                onChange={handleOnPhilosophyChange}
                                style={{ borderColor: `${error.field === "textDut2" ? "red" : "var(--border-color)"}` }}
                            ></textarea>
                        </div>
                        {error.field === "textDut2" &&
                            <label className="form-label" style={{ color: "red" }}>{error.note}</label>
                        }
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="primary"
                        className="me-1"
                        onClick={() => handlePhilosophy()}
                        disabled={isLoading}
                    >
                        {
                            isLoading ?
                                <>
                                    <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-1" />
                                    Yükleniyor...
                                </> :
                                <>
                                    Kaydet
                                </>
                        }
                    </Button>
                </Modal.Footer>
            </Modal>
            <Modal
                centered
                show={isDelete}
                onHide={() => {
                    setIsDelete(null);
                    setIsLoading(false);
                }}
            >
                <Modal.Header closeButton>
                    <Modal.Title className="fw-bold">Uyarı</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="mb-3">
                        <div className="form-label">
                            Bu öğeyi silmek istediğinize emin misiniz?
                        </div>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="danger"
                        className="me-1 text-white"
                        onClick={() => handleDelete(isDelete.id)}
                        disabled={isLoading}
                    >
                        {
                            isLoading ?
                                <>
                                    <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-1" />
                                    Siliniyor...
                                </> :
                                <>
                                    <i className="icofont-ui-delete"></i>
                                    &ensp;
                                    Sil
                                </>
                        }
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    )
}

function SortableItem({ id, children }) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
        cursor: "grab"
    };

    return (
        <div
            ref={setNodeRef}
            style={style}
            {...attributes}
            {...listeners}
            className="col-xxl-3 col-xl-4 col-lg-4 col-md-6 col-sm-6"
        >
            {children}
        </div>
    );
}

export default PhilosophyList;